import { Checkbox } from "antd/es";
import { CheckboxProps } from "antd/es/checkbox";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { PureComponent, ReactNode } from "react";
import { CommonProps } from "../../../components/web-components/common/common-props";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import DynamicStyleManager from "../../../shared/utilty/dynamic-style-manager";

declare let window: Window & { kuika: any };

interface IKCheckboxProps extends Omit<CheckboxProps, "onChange"> {
  onChange?: (value?: boolean) => void;
  boxColor?: string;
  boxColorOnChecked?: string;
  boxColorOnDisabled?: string;
  checkIconColor?: string;
  label?: string;
  defaultValue?: boolean;
}

interface IKCheckboxState {
  checked: boolean;
}

class KCheckbox extends PureComponent<IKCheckboxProps & CommonProps, IKCheckboxState> {
  private uniqueKey = Guid.create();

  private style = new DynamicStyleManager(`kcheckbox_${this.uniqueKey}`);

  constructor(props: IKCheckboxProps) {
    super(props);
    this.state = {
      checked: this.getInitialChecked()
    };
  }

  componentDidMount = () => {
    this.setDynamicStyle();
  };

  componentDidUpdate(prevProps: IKCheckboxProps) {
    if (this.props.checked === null) {
      this.handleCheckboxChange(false);
    }
    if (this.props.checked !== prevProps.checked && this.props.checked !== undefined) {
      this.setState({ checked: !!this.props.checked });
    } else if (this.props.value !== prevProps.value && this.props.value !== undefined) {
      this.setState({ checked: !!this.props.value });
    }
  }

  getInitialChecked = () => {
    const value = this.props.checked ?? this.props.value ?? this.props.defaultValue;
    if (value === undefined || value === null) return false;
    if (value !== undefined) {
      if (value.toString() === "") return false;
      if (value.toString() === "1") return true;
      if (value.toString() === "0") return false;
      if (value.toString() === "true") return true;
      if (value.toString() === "false") return false;
    }
  };

  setDynamicStyle = () => {
    this.style.setDynamicCss(this.getDynamicCss());
  };

  getUniqueClassName = () => {
    let result = "";

    if (!this.uniqueKey) {
      return result;
    }
    result = `k_checkbox_${this.uniqueKey.toString().substring(0, 8)}`;
    return result;
  };

  getDynamicCss() {
    const className: string = this.getUniqueClassName();
    let { boxColor, boxColorOnChecked, boxColorOnDisabled, checkIconColor } = this.props;
    if (!className || className.length === 0) {
      return "";
    }

    boxColor = boxColor || "#fffff";
    boxColorOnChecked = boxColorOnChecked || "#246dff";
    boxColorOnDisabled = boxColorOnDisabled || "#bbbbbb";
    checkIconColor = checkIconColor || "#246dff";

    let result = "";
    result += `.k_checkbox.${className.trim()} .ant-checkbox .ant-checkbox-inner {
          background-color: ${boxColor} !important;
          border-color: ${boxColorOnChecked};

        }`;

    result += `.k_checkbox.${className.trim()} .ant-checkbox-checked::after {
          border-color: ${boxColorOnChecked} !important;
        }`;

    result += `.k_checkbox.${className.trim()} .ant-checkbox-checked .ant-checkbox-inner {
          background-color: ${boxColorOnChecked} !important;
        }`;

    result += `.k_checkbox.${className.trim()} .ant-checkbox-disabled .ant-checkbox-inner {
          background-color: ${boxColorOnDisabled} !important;
        }`;

    result += `.k_checkbox.${className.trim()} .ant-checkbox .ant-checkbox-inner::after {
         border-color: ${checkIconColor} !important;
        }`;

    return result;
  }

  getProps = () => {
    const style = _.cloneDeep(this.props.style);

    if (style.display === "block") {
      style.display = "flex";
    } else {
      style.display = "inline-flex";
    }

    switch (style.textAlign as string) {
      case "-webkit-right": {
        style.justifyContent = "flex-end";
        break;
      }
      case "-webkit-center": {
        style.justifyContent = "center";
        break;
      }
      case "-webkit-left":
      default: {
        style.justifyContent = "flex-start";
        break;
      }
    }

    return {
      className: `k_checkbox ${this.getUniqueClassName()}`,
      style
    };
  };

  handleCheckboxChange = (checked: boolean) => {
    this.setState({ checked });
    if (this.props.onChange) {
      this.props.onChange(checked);
    }
  };

  getChecked = () => {
    return this.state.checked;
  };

  render(): ReactNode {
    return (
      <div {...this.getProps()}>
        <Checkbox
          onChange={(e) => this.handleCheckboxChange(e.target.checked)}
          checked={this.getChecked()}
          disabled={this.props.editability === "disabled"}
        >
          {(this.props.label ?? this.props.children) || undefined}
        </Checkbox>
      </div>
    );
  }
}

const kCheckbox = withCommonEvents(KCheckbox);
export { kCheckbox as KCheckbox };
